import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import NotificationAdminService from '../services/notificationAdminService';

const emptyForm = {
  code: '',
  displayName: '',
  description: '',
  displayOrder: 999,
  enabled: true,
};

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ display: 'block', color: 'text.secondary', letterSpacing: 1, mb: 1.5, mt: 0.5 }}
  >
    {children}
  </Typography>
);

const NotificationCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await NotificationAdminService.listCategories();
      setCategories(res?.data || []);
    } catch (e) {
      notify(e?.message || 'Failed to load categories', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const openCreate = () => {
    setForm(emptyForm);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openEdit = (category) => {
    setForm({
      id: category.id,
      code: category.code,
      displayName: category.displayName || '',
      description: category.description || '',
      displayOrder: category.displayOrder ?? 999,
      enabled: category.enabled ?? true,
    });
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleField = (key) => (e) => {
    const value = key === 'displayOrder' 
      ? parseInt(e.target.value, 10) || 0 
      : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSave = async () => {
    if (!form.displayName.trim()) {
      notify('Display name is required', 'error');
      return;
    }

    if (dialogMode === 'create' && !form.code.trim()) {
      notify('Code is required', 'error');
      return;
    }

    setSaving(true);
    try {
      if (dialogMode === 'create') {
        await NotificationAdminService.createCategory(form);
        notify('Category created successfully');
      } else {
        await NotificationAdminService.updateCategory(form.id, form);
        notify('Category updated successfully');
      }
      setDialogOpen(false);
      loadCategories();
    } catch (e) {
      notify(e?.message || 'Failed to save category', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Delete category "${category.displayName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await NotificationAdminService.deleteCategory(category.id);
      notify('Category deleted successfully');
      loadCategories();
    } catch (e) {
      notify(e?.message || 'Failed to delete category', 'error');
    }
  };

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>Notification Categories</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage notification categories used by templates and campaigns
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Category
        </Button>
      </Box>

      {/* Categories table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    No categories found.
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((cat) => (
                  <TableRow key={cat.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">{cat.code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{cat.displayName}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {cat.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={cat.isSystem ? 'System' : 'Custom'} 
                        color={cat.isSystem ? 'default' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>{cat.displayOrder}</TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={cat.enabled ? 'Enabled' : 'Disabled'} 
                        color={cat.enabled ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(cat)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={cat.isSystem ? 'System categories cannot be deleted' : 'Delete'}>
                        <span>
                          <IconButton 
                            size="small" 
                            color="error" 
                            disabled={cat.isSystem}
                            onClick={() => handleDelete(cat)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Category form dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create New Category' : `Edit Category — ${form.code}`}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <SectionLabel>Category Details</SectionLabel>
          <Stack spacing={2.5}>
            
            {/* Code (only for create) */}
            {dialogMode === 'create' && (
              <TextField
                label="Code"
                fullWidth
                required
                value={form.code}
                onChange={handleField('code')}
                helperText="Uppercase letters, numbers, and underscores (e.g., ORDERS, SPECIAL_EVENTS)"
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            )}

            {/* Display Name */}
            <TextField
              label="Display Name"
              fullWidth
              required
              value={form.displayName}
              onChange={handleField('displayName')}
              helperText="User-friendly name shown in dropdowns"
            />

            {/* Description */}
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={form.description}
              onChange={handleField('description')}
              helperText="Optional description of when to use this category"
            />

            {/* Display Order */}
            <TextField
              label="Display Order"
              fullWidth
              type="number"
              value={form.displayOrder}
              onChange={handleField('displayOrder')}
              helperText="Lower numbers appear first in dropdowns"
            />

            {/* Enabled toggle */}
            <FormControlLabel
              control={
                <Switch 
                  checked={form.enabled} 
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                />
              }
              label={form.enabled ? 'Enabled (visible in dropdowns)' : 'Disabled (hidden from dropdowns)'}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationCategoriesPage;
